import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import type { RootState } from '@generalTypes/rootStateTypes';
import {
  selectCanPublishDocument,
  selectIsDocumentPublished,
  selectIsDocumentReadOnly,
  selectIsDocumentSaving,
} from '@newStore/documentUI/documentUISelectors';
import { selectDocumentHasIssuedField } from '@newStore/documentUI/nodeTypeConfigSelectors';

interface SaveButtonProps {
  onSave: () => void;
  onSaveAndPublish: () => void;
  onSaveAndSubmit: () => void;
}

const SaveButton: React.FC<SaveButtonProps> = ({ onSave, onSaveAndPublish, onSaveAndSubmit }) => {
  // Get data from Redux state
  const canPublish = useSelector(selectCanPublishDocument);
  const mode = useSelector((state: RootState) => state.documentUI.mode);
  const itemsToSave = useSelector((state: RootState) => state.document.viewModel.itemsToSave);
  const isValid = useSelector((state: RootState) => state.document.viewModel.isValid);
  const isSaving = useSelector(selectIsDocumentSaving);

  const isPublished = useSelector(selectIsDocumentPublished);
  const isReadOnlyDocument = useSelector(selectIsDocumentReadOnly);
  const hasIssuedField = useSelector(selectDocumentHasIssuedField);

  const shouldShowPublishChangesButton = () => {
    // Webmasters: single "Publish changes" button ONLY for published documents
    return isPublished && (mode === 'EDIT' || mode === 'REVIEW') && canPublish;
  };

  const shouldShowSaveAndPublishButton = () => {
    return !isPublished && mode === 'EDIT' && canPublish;
  };

  const shouldShowSaveAndSubmitButton = () => {
    // Authors: "Save + submit" option when they cannot publish
    return !canPublish && !isReadOnlyDocument && hasIssuedField;
  };

  const shouldShowDropdown = () => {
    return shouldShowSaveAndPublishButton() || shouldShowSaveAndSubmitButton();
  };

  const isDisabled = itemsToSave === 0 || !isValid || isReadOnlyDocument || isSaving;

  const handleSave = () => {
    if (!isDisabled) {
      onSave();
    }
  };

  const handleSaveAndPublish = () => {
    if (!isDisabled) {
      onSaveAndPublish();
    }
  };

  const handleSaveAndSubmit = () => {
    if (!isDisabled) {
      onSaveAndSubmit();
    }
  };

  if (!shouldShowDropdown()) {
    // Single button: "Save" or "Publish Changes"
    return (
      <button className="btn btn-primary buttonRelative" onClick={handleSave} disabled={isDisabled}>
        <div className={isSaving ? 'buttonInvisible' : ''}>
          <span
            className="glyphicon glyphicon-floppy-disk"
            style={{ margin: '0 5px', verticalAlign: 'top' }}
          />
          <span>
            {shouldShowPublishChangesButton() ? 'Wijzigingen publiceren' : 'Wijzigingen opslaan'}
          </span>
        </div>
        <div className={!isSaving ? 'buttonInvisible' : 'spinner-icon center'} />
      </button>
    );
  }

  // Dropdown with multiple save options
  return (
    <div className="dropdown">
      <button
        className="btn btn-primary buttonRelative dropdown-toggle"
        data-toggle="dropdown"
        disabled={isDisabled}
      >
        <div className={isSaving ? 'buttonInvisible' : ''}>
          <span
            className="glyphicon glyphicon-floppy-disk"
            style={{ marginRight: '5px', verticalAlign: 'top' }}
          />
          <span>Wijzigingen opslaan</span>
          <b className="caret" style={{ marginLeft: '5px' }} />
        </div>
        <div className={!isSaving ? 'buttonInvisible' : 'spinner-icon center'} />
      </button>

      <ul className="dropdown-menu dropdown-menu-right">
        <li>
          <a onClick={onSave}>
            <span>Wijzigingen opslaan</span>
          </a>
        </li>

        {shouldShowSaveAndPublishButton() && (
          <li>
            <a onClick={handleSaveAndPublish}>
              <span>Wijzigingen opslaan en publiceren</span>
            </a>
          </li>
        )}

        {shouldShowSaveAndSubmitButton() && (
          <li>
            <a onClick={handleSaveAndSubmit}>
              <span>Wijzigingen opslaan en naar eindredactie</span>
            </a>
          </li>
        )}
      </ul>
    </div>
  );
};

export default SaveButton;

SaveButton.displayName = 'SaveButton';

SaveButton.propTypes = {
  onSave: PropTypes.func.isRequired,
  onSaveAndPublish: PropTypes.func.isRequired,
  onSaveAndSubmit: PropTypes.func.isRequired,
};
